// Provider injected into the in-app browser; mirrors extension/src/script.js.
export const injectedProviderCode = `(function () {
  if (window.dwallet?.isDWallet) {
    // Already installed; re-announce so the host stops retrying.
    try {
      sendToHost({ id: 0, method: 'providerReady' });
    } catch (error) {}
    return;
  }

  const BASE58_ALPHABET =
    '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';
  const BASE58_MAP = new Map(
    [...BASE58_ALPHABET].map((char, index) => [char, index]),
  );

  function encodeBase58(bytes) {
    if (!(bytes instanceof Uint8Array)) {
      bytes = new Uint8Array(bytes);
    }

    if (bytes.length === 0) return '';

    let zeros = 0;
    while (zeros < bytes.length && bytes[zeros] === 0) zeros++;

    const digits = [0];

    for (let i = zeros; i < bytes.length; i++) {
      let carry = bytes[i];

      for (let j = 0; j < digits.length; j++) {
        carry += digits[j] << 8;
        digits[j] = carry % 58;
        carry = Math.floor(carry / 58);
      }

      while (carry > 0) {
        digits.push(carry % 58);
        carry = Math.floor(carry / 58);
      }
    }

    let encoded = '1'.repeat(zeros);

    for (let i = digits.length - 1; i >= 0; i--) {
      encoded += BASE58_ALPHABET[digits[i]];
    }

    return encoded;
  }

  function decodeBase58(value) {
    if (typeof value !== 'string') {
      throw new Error('Expected base58 string');
    }

    if (value.length === 0) return new Uint8Array();

    let zeros = 0;
    while (zeros < value.length && value[zeros] === '1') zeros++;

    const bytes = [0];

    for (let i = zeros; i < value.length; i++) {
      const char = value[i];
      const digit = BASE58_MAP.get(char);

      if (digit === undefined) {
        throw new Error('Invalid base58 character: ' + char);
      }

      let carry = digit;

      for (let j = 0; j < bytes.length; j++) {
        carry += bytes[j] * 58;
        bytes[j] = carry & 0xff;
        carry >>= 8;
      }

      while (carry > 0) {
        bytes.push(carry & 0xff);
        carry >>= 8;
      }
    }

    const decoded = new Uint8Array(zeros + bytes.length);

    for (let i = 0; i < zeros; i++) {
      decoded[i] = 0;
    }

    for (let i = 0; i < bytes.length; i++) {
      decoded[decoded.length - 1 - i] = bytes[i];
    }

    return decoded;
  }

  function normalizeBytes(value) {
    if (value instanceof Uint8Array) return value;

    if (ArrayBuffer.isView(value)) {
      return new Uint8Array(value.buffer, value.byteOffset, value.byteLength);
    }

    if (value instanceof ArrayBuffer) {
      return new Uint8Array(value);
    }

    if (Array.isArray(value)) {
      return new Uint8Array(value);
    }

    throw new Error('Expected bytes');
  }

  function makePublicKey(base58) {
    if (!base58) return null;

    return Object.freeze({
      toBase58() {
        return base58;
      },
      toString() {
        return base58;
      },
      toJSON() {
        return base58;
      },
      equals(other) {
        if (!other) return false;

        const otherBase58 =
          typeof other === 'string'
            ? other
            : other.toBase58?.() || other.toString?.();

        return base58 === otherBase58;
      },
    });
  }

  function randomId() {
    return window.crypto && window.crypto.randomUUID
      ? window.crypto.randomUUID()
      : String(Date.now()) + '-' + Math.random();
  }
  let publicKey = null;
  let isConnected = false;

  const listeners = new Map();

  function emit(eventName, payload) {
    const eventListeners = listeners.get(eventName);
    if (!eventListeners) return;

    for (const listener of eventListeners) {
      try {
        listener(payload);
      } catch (error) {
        console.error(error);
      }
    }
  }

  // Send over the Cordova InAppBrowser bridge; the host replies via window.postMessage.
  function sendToHost(message) {
    message.metadata = { dappTitle: document.title };
    webkit.messageHandlers.cordova_iab.postMessage(JSON.stringify(message));
  }

  function requestBridge(method, params = {}) {
    const id = randomId();

    return new Promise((resolve, reject) => {
      const listener = (event) => {
        if (event.source !== window) return;

        const response = event.data;

        if (!response || response.id !== id) return;

        window.removeEventListener('message', listener);

        if (response.error) {
          const error =
            response.error instanceof Error
              ? response.error
              : new Error(
                  typeof response.error === 'string'
                    ? response.error
                    : response.error.message || 'DWallet request failed',
                );

          reject(error);
          return;
        }

        resolve(response.result ?? response.params ?? response);
      };

      window.addEventListener('message', listener);

      sendToHost({ id, method, params });
    });
  }

  function attachSignatureToTransaction(transaction, walletPublicKey, signature) {
    const signatureBytes = normalizeBytes(signature);

    if (typeof transaction.addSignature === 'function') {
      transaction.addSignature(walletPublicKey, signatureBytes);
      return transaction;
    }

    if (Array.isArray(transaction.signatures)) {
      const index = transaction.signatures.findIndex((entry) =>
        walletPublicKey.equals(entry.publicKey),
      );

      if (index >= 0) {
        transaction.signatures[index].signature = signatureBytes;
        return transaction;
      }
    }

    throw new Error('Transaction object does not support addSignature');
  }

  const provider = {
    isDWallet: true,

    get publicKey() {
      return publicKey;
    },

    get isConnected() {
      return isConnected;
    },

    async connect() {
      const response = await requestBridge('connect');

      const publicKeyBase58 =
        response?.publicKey ||
        response?.params?.publicKey ||
        response?.result?.publicKey;

      if (!publicKeyBase58) {
        throw new Error('DWallet connect response missing publicKey');
      }

      publicKey = makePublicKey(publicKeyBase58);
      isConnected = true;

      emit('connect', publicKey);

      return { publicKey };
    },

    async disconnect() {
      await requestBridge('disconnect');

      publicKey = null;
      isConnected = false;

      emit('disconnect');

      return undefined;
    },

    async signTransaction(transaction) {
      if (!publicKey) {
        await provider.connect();
      }

      if (!transaction?.serializeMessage) {
        throw new Error('Invalid transaction: missing serializeMessage()');
      }

      const message = encodeBase58(transaction.serializeMessage());

      const response = await requestBridge('signTransaction', { message });

      if (!response?.signature) {
        throw new Error('DWallet signTransaction response missing signature');
      }

      const signature =
        typeof response.signature === 'string'
          ? decodeBase58(response.signature)
          : normalizeBytes(response.signature);

      return attachSignatureToTransaction(transaction, publicKey, signature);
    },

    async signAllTransactions(transactions) {
      if (!publicKey) {
        await provider.connect();
      }

      if (!Array.isArray(transactions)) {
        throw new Error('signAllTransactions expects an array');
      }

      const messages = transactions.map((transaction) => {
        if (!transaction?.serializeMessage) {
          throw new Error('Invalid transaction: missing serializeMessage()');
        }

        return encodeBase58(transaction.serializeMessage());
      });

      const response = await requestBridge('signAllTransactions', { messages });

      if (!Array.isArray(response?.signatures)) {
        throw new Error(
          'DWallet signAllTransactions response missing signatures',
        );
      }

      if (response.signatures.length !== transactions.length) {
        throw new Error('DWallet returned incorrect signature count');
      }

      return transactions.map((transaction, index) => {
        const rawSignature = response.signatures[index];

        const signature =
          typeof rawSignature === 'string'
            ? decodeBase58(rawSignature)
            : normalizeBytes(rawSignature);

        return attachSignatureToTransaction(transaction, publicKey, signature);
      });
    },

    async signMessage(message, display = 'utf8') {
      if (!publicKey) {
        await provider.connect();
      }

      const data = normalizeBytes(message);

      // Host decodes base58 'message'; mirror that contract here.
      const response = await requestBridge('sign', {
        message: encodeBase58(data),
        display,
      });

      if (!response?.signature) {
        throw new Error('DWallet signMessage response missing signature');
      }

      const signature =
        typeof response.signature === 'string'
          ? decodeBase58(response.signature)
          : normalizeBytes(response.signature);

      return {
        signature,
        publicKey,
      };
    },

    async request(args) {
      if (!args || typeof args.method !== 'string') {
        throw new Error('Invalid request args');
      }

      switch (args.method) {
        case 'connect':
          return provider.connect(args.params);

        case 'disconnect':
          return provider.disconnect();

        case 'signTransaction':
          return provider.signTransaction(args.params?.transaction);

        case 'signAllTransactions':
          return provider.signAllTransactions(args.params?.transactions);

        case 'signMessage':
        case 'sign':
          return provider.signMessage(
            args.params?.message ?? args.params?.data,
            args.params?.display ?? args.params?.encoding ?? 'utf8',
          );

        default:
          return requestBridge(args.method, args.params || {});
      }
    },

    on(eventName, listener) {
      if (!listeners.has(eventName)) {
        listeners.set(eventName, new Set());
      }

      listeners.get(eventName).add(listener);
      return this;
    },

    removeListener(eventName, listener) {
      listeners.get(eventName)?.delete(listener);
      return this;
    },

    off(eventName, listener) {
      return this.removeListener(eventName, listener);
    },

    // Legacy passthrough; host replies directly via window.postMessage.
    postMessage(message) {
      sendToHost(message);
    },
  };

  Object.defineProperty(window, 'dwallet', {
    value: provider,
    writable: false,
    configurable: false,
  });

  window.dispatchEvent(new Event('dwallet#initialized'));

  // Handshake: host stops retrying on receipt. No reply expected.
  try {
    sendToHost({ id: 0, method: 'providerReady' });
  } catch (error) {}
})();`;
